import { useCaregiverSpecialists } from "@/hooks/use-caregiver-specialists";
import { SpecialistCard } from "./SpecialistCard";
import { useTranslations } from "@/i18n/hooks";
import { Skeleton } from "@/components/ui/skeleton";
import { Users } from "lucide-react";

interface SpecialistPanelProps {
  onChatClick: () => void;
  onVideoCallClick?: (specialistId: string, specialistName: string) => void;
}

export function SpecialistPanel({ onChatClick, onVideoCallClick }: SpecialistPanelProps) {
  const { data: specialists = [], isLoading, error } = useCaregiverSpecialists();
  const t = useTranslations('dashboard');

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3 sm:space-y-6">
        <div className="text-center">
          <h2 className="text-lg sm:text-2xl font-bold text-foreground">{t('sleep_specialists')}</h2>
          <p className="text-2xs sm:text-base text-muted-foreground mt-0.5 sm:mt-1">{t('connect_with_experts')}</p>
        </div>
        <div className="space-y-3 sm:space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="bg-card rounded-xl border border-border p-3 sm:p-5">
              <div className="flex items-center gap-3 sm:gap-4">
                <Skeleton className="h-10 w-10 sm:h-12 sm:w-12 rounded-full" />
                <div className="space-y-1.5 sm:space-y-2 flex-1">
                  <Skeleton className="h-4 sm:h-5 w-3/4" />
                  <Skeleton className="h-3 sm:h-4 w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-3 sm:space-y-6">
        <div className="text-center">
          <h2 className="text-lg sm:text-2xl font-bold text-foreground">{t('sleep_specialists')}</h2>
          <p className="text-2xs sm:text-base text-muted-foreground mt-0.5 sm:mt-1">{t('connect_with_experts')}</p>
        </div>
        <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 sm:p-6 text-center">
          <p className="text-destructive text-sm sm:text-base">{t('error_loading_specialists')}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (specialists.length === 0) {
    return (
      <div className="space-y-3 sm:space-y-6">
        <div className="text-center">
          <h2 className="text-lg sm:text-2xl font-bold text-foreground">{t('sleep_specialists')}</h2>
          <p className="text-2xs sm:text-base text-muted-foreground mt-0.5 sm:mt-1">{t('connect_with_experts')}</p>
        </div>
        <div className="bg-card rounded-xl border border-border p-6 sm:p-8 text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <Users className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground text-sm sm:text-base">{t('no_specialists_available')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-lg sm:text-2xl font-bold text-foreground">{t('sleep_specialists')}</h2>
        <p className="text-2xs sm:text-base text-muted-foreground mt-0.5 sm:mt-1">{t('connect_with_experts')}</p>
      </div>

      {/* Specialist Cards */}
      <div className="space-y-3 sm:space-y-4">
        {specialists.map((specialist) => (
          <SpecialistCard 
            key={specialist.id} 
            specialist={specialist} 
            onChatClick={onChatClick}
            onVideoCallClick={onVideoCallClick}
          />
        ))}
      </div>
    </div>
  );
}
